/* -------------------------------------------------------------------------- */
/*  src/components/Chart.jsx – Multi-Select-Highlight + Fallback für leere    */
/*  Serien (z. B. “André Brinkmann”)                                          */
/* -------------------------------------------------------------------------- */
import React, { useState, useCallback, useMemo } from 'react';
import * as PropTypes from 'prop-types';

import {
  Chart as KendoChart,
  ChartSeries,
  ChartSeriesItem,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartLegend,
  ChartTooltip
} from '@progress/kendo-react-charts';
import { groupBy, filterBy } from '@progress/kendo-data-query';
import { useInternationalization } from '@progress/kendo-react-intl';
import { locales }   from './../resources/locales';
import { drawing }   from '@progress/kendo-drawing';

import './../styles/dashboard.css';

const MONTH_FORMAT   = 'MMMM yyyy';
const COMPACT_FORMAT = 'MM yyyy';
const LEGEND_COL_W   = 220;
const LEGEND_ROW_H   = 22;

/* Hex → rgba -------------------------------------------------------------- */
const toRgba = (hex = '#888', alpha = 1) => {
  const h = hex.replace('#', '');
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
};

/* LegendItemVisual: fixe Spaltenbreite ------------------------------------ */
const legendItemVisual = (e) => {
  const g = new drawing.Group();
  g.append(
    new drawing.Path({ stroke: { color: 'transparent' }, fill: { color: 'transparent' } })
      .moveTo(0, 0)
      .lineTo(LEGEND_COL_W, 0)
      .lineTo(LEGEND_COL_W, LEGEND_ROW_H)
      .lineTo(0, LEGEND_ROW_H)
      .close()
  );
  g.append(e.createVisual());
  return g;
};

/* -------------------------------------------------------------------------- */
export const Chart = ({
  data,
  groupByField,
  seriesCategoryField,
  seriesField,
  filterStart,
  filterEnd,
  seriesType,
  groupResourceData,
  groupTextField,
  groupColorField,
  onRefresh
}) => {
  const intl = useInternationalization();

  /* -------- Daten filtern & gruppieren -------------------------------- */
  const filtered = filterBy(data, {
    logic: 'and',
    filters: [
      { field: seriesCategoryField, operator: 'gte', value: filterStart },
      { field: seriesCategoryField, operator: 'lt',  value: filterEnd  }
    ]
  });
  const grouped = groupBy(filtered, [{ field: groupByField }]);

  /* -------- Map: Legend-Label → ID (für leere Serien) ----------------- */
  const nameToId = useMemo(() => {
    const m = new Map();
    grouped.forEach(({ value }) => {
      const res = groupResourceData?.find(r => r[groupByField] === value) || {};
      m.set(res[groupTextField] || value, value);
    });
    return m;
  }, [grouped, groupResourceData, groupByField, groupTextField]);

  /* -------- Multi-Select Fokus-State ---------------------------------- */
  const [focusIds, setFocusIds] = useState(() => new Set());

  const toggleFocus = useCallback((id) => {
    setFocusIds(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  }, []);

  const onLegendItemClick = useCallback((ev) => {
    ev.preventDefault();                                  // default-hide verhindern
    let id = ev.series?.data?.[0]?.[groupByField];
    if (id === undefined) {
      id = nameToId.get(ev.series?.name);                 // Fallback über Label
    }
    if (id !== undefined) toggleFocus(id);
  }, [groupByField, nameToId, toggleFocus]);

  /* -------- Tooltip ---------------------------------------------------- */
  const Tooltip = ({ point }) => (
    <div>
      <div style={{ textTransform: 'uppercase' }}>
        {intl.formatDate(point.category, MONTH_FORMAT)}
      </div>
      <div style={{ textAlign: 'center' }}>
        {point.series.name}: {intl.formatNumber(point.value, { locale: locales.locale })}
      </div>
    </div>
  );

  /* -------- Render ----------------------------------------------------- */
  return (
    <KendoChart
      style={{ height: 400 }}
      onRefresh={onRefresh}
      onLegendItemClick={onLegendItemClick}
    >
      <ChartLegend
        position="bottom"
        orientation="horizontal"
        align="start"
        background="#f4f5f8"
        margin={{ top: 40 }}
        padding={{ left: 80 }}
        labels={{ padding: { right: 0 }, color: '#424242' }}
        inactiveItems={{
          labels:  { color: 'orange', opacity: 1 },
          markers: { color: 'orange', opacity: 1 }
        }}
        item={{ visual: legendItemVisual }}
      />

      <ChartTooltip render={Tooltip} />

      <ChartSeries>
        {grouped.map(({ value, items }) => {
          const res       = groupResourceData?.find(r => r[groupByField] === value) || {};
          const baseColor = res[groupColorField] || '#888';

          const dimOthers = focusIds.size > 0 && !focusIds.has(value);
          const color     = dimOthers ? toRgba(baseColor, 0.1) : baseColor;

          return (
            <ChartSeriesItem
              key={value}
              name={res[groupTextField] || value}
              type={seriesType}
              field={seriesField}
              categoryField={seriesCategoryField}
              data={items}
              color={color}
              aggregate="sum"
            />
          );
        })}
      </ChartSeries>

      {/* Achsen ---------------------------------------------------------- */}
      <ChartCategoryAxis>
        <ChartCategoryAxisItem
          baseUnit="months"
          labels={{
            visible: true,
            rotation: 'auto',
            color: '#424242',
            dateFormats: {
              months: window.innerWidth >= 480 ? MONTH_FORMAT : COMPACT_FORMAT
            }
          }}
          majorGridLines={{ visible: true, color: '#e0e0e0', width: 1 }}
        />
      </ChartCategoryAxis>

      <ChartValueAxis>
        <ChartValueAxisItem
          min={0}
          labels={{ visible: true, color: '#424242', format: 'n0' }}
          majorGridLines={{ visible: true, color: '#e0e0e0', width: 1 }}
        />
      </ChartValueAxis>
    </KendoChart>
  );
};

Chart.displayName = 'Chart';

Chart.propTypes = {
  data:                PropTypes.array.isRequired,
  groupByField:        PropTypes.string.isRequired,
  seriesCategoryField: PropTypes.string.isRequired,
  seriesField:         PropTypes.string.isRequired,
  filterStart:         PropTypes.instanceOf(Date).isRequired,
  filterEnd:           PropTypes.instanceOf(Date).isRequired,
  seriesType:          PropTypes.string,
  groupResourceData:   PropTypes.array,
  groupTextField:      PropTypes.string,
  groupColorField:     PropTypes.string,
  onRefresh:           PropTypes.func
};
